import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_BASE_URL, DEFAULT_HEADERS, DEFAULT_API_KEY } from '../config/environment'

// API Configuration types
export interface APIEndpoint {
  id: string
  name: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  path: string
  description: string
  category: string
  requiresAuth: boolean
  samplePayload?: any
  parameters?: APIParameter[]
  queryParameters?: APIParameter[]
  headerParameters?: APIParameter[]
}

export interface APIParameter {
  name: string
  type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  required: boolean
  description: string
  example?: any
}

export interface APIRequest {
  id: string
  endpoint: APIEndpoint
  baseUrl: string
  headers: Record<string, string>
  requestBody?: any
  queryParams?: Record<string, string>
  timestamp: number
}

export interface APIResponse {
  id: string
  requestId: string
  endpoint: APIEndpoint
  status: number
  statusText: string
  headers: Record<string, string>
  data: any
  responseTime: number
  size: number
  timestamp: number
  error?: string
}

// API Request Store
interface APIRequestState {
  currentRequest: Partial<APIRequest>
  selectedEndpoint: APIEndpoint | null
  baseUrl: string
  headers: Record<string, string>
  requestBody: any
  queryParams: Record<string, string>
  viewMode: 'form' | 'json'
  isLoading: boolean
  
  // Actions
  setSelectedEndpoint: (endpoint: APIEndpoint | null) => void
  setBaseUrl: (url: string) => void
  setHeaders: (headers: Record<string, string>) => void
  setRequestBody: (body: any) => void
  setQueryParams: (params: Record<string, string>) => void
  setViewMode: (mode: 'form' | 'json') => void
  setIsLoading: (loading: boolean) => void
  addHeader: (key: string, value: string) => void
  removeHeader: (key: string) => void
  addQueryParam: (key: string, value: string) => void
  removeQueryParam: (key: string) => void
  resetRequest: () => void
}

export const useAPIRequestStore = create<APIRequestState>()(
  persist(
    (set, get) => ({
      currentRequest: {},
      selectedEndpoint: null,
      baseUrl: DEFAULT_BASE_URL,
      headers: {
        ...DEFAULT_HEADERS
      },
      requestBody: null,
      queryParams: {
        api_key: DEFAULT_API_KEY
      },
      viewMode: 'form',
      isLoading: false,

      setSelectedEndpoint: (endpoint) => set({ selectedEndpoint: endpoint }),
      setBaseUrl: (url) => set({ baseUrl: url }),
      setHeaders: (headers) => set({ headers }),
      setRequestBody: (body) => set({ requestBody: body }),
      setQueryParams: (params) => set({ queryParams: params }),
      setViewMode: (mode) => set({ viewMode: mode }),
      setIsLoading: (loading) => set({ isLoading: loading }),

      addHeader: (key, value) => {
        const { headers } = get()
        set({ headers: { ...headers, [key]: value } })
      },

      removeHeader: (key) => {
        const { headers } = get()
        const newHeaders = { ...headers }
        delete newHeaders[key]
        set({ headers: newHeaders })
      },

      addQueryParam: (key, value) => {
        const { queryParams } = get()
        set({ queryParams: { ...queryParams, [key]: value } })
      },

      removeQueryParam: (key) => {
        const { queryParams } = get()
        const newParams = { ...queryParams }
        delete newParams[key]
        set({ queryParams: newParams })
      },

      resetRequest: () => set({
        currentRequest: {},
        selectedEndpoint: null,
        requestBody: null,
        queryParams: {},
        viewMode: 'form'
      })
    }),
    {
      name: 'api-request-storage-v2',
      partialize: (state) => ({
        headers: state.headers,
        viewMode: state.viewMode
      })
    }
  )
)

// API Response Store
interface APIResponseState {
  currentResponse: APIResponse | null
  responses: APIResponse[]
  activeView: 'json' | 'raw'
  
  // Actions
  setCurrentResponse: (response: APIResponse | null) => void
  addResponse: (response: APIResponse) => void
  setActiveView: (view: 'json' | 'raw') => void
  clearResponses: () => void
}

export const useAPIResponseStore = create<APIResponseState>((set) => ({
  currentResponse: null,
  responses: [],
  activeView: 'json',

  setCurrentResponse: (response) => set({ currentResponse: response }),
  addResponse: (response) => set((state) => ({ 
    responses: [response, ...state.responses].slice(0, 50) // Keep last 50 responses
  })),
  setActiveView: (view) => set({ activeView: view }),
  clearResponses: () => set({ responses: [], currentResponse: null })
}))

// Request History Store
interface RequestHistoryState {
  history: APIRequest[]
  savedRequests: APIRequest[]
  
  // Actions
  addToHistory: (request: APIRequest) => void
  saveRequest: (request: APIRequest, name?: string) => void
  removeFromHistory: (requestId: string) => void
  removeSavedRequest: (requestId: string) => void
  clearHistory: () => void
}

export const useRequestHistoryStore = create<RequestHistoryState>()(
  persist(
    (set) => ({
      history: [],
      savedRequests: [],

      addToHistory: (request) => set((state) => ({
        history: [request, ...state.history].slice(0, 20) // Keep last 20 requests
      })),

      saveRequest: (request, name) => set((state) => ({
        savedRequests: [...state.savedRequests, { ...request, name }]
      })),

      removeFromHistory: (requestId) => set((state) => ({
        history: state.history.filter(r => r.id !== requestId)
      })),

      removeSavedRequest: (requestId) => set((state) => ({
        savedRequests: state.savedRequests.filter(r => r.id !== requestId)
      })),

      clearHistory: () => set({ history: [], savedRequests: [] })
    }),
    {
      name: 'request-history-storage'
    }
  )
)

// UI State Store
interface UIState {
  leftPanelWidth: number
  rightPanelWidth: number
  bottomPanelHeight: number
  showDocumentation: boolean
  
  // Actions
  setLeftPanelWidth: (width: number) => void
  setRightPanelWidth: (width: number) => void
  setBottomPanelHeight: (height: number) => void
  setShowDocumentation: (show: boolean) => void
  adjustPanelWidth: (panel: 'left' | 'right', delta: number, containerWidth: number) => void
  adjustBottomPanelHeight: (delta: number, containerHeight: number) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      leftPanelWidth: 35,
      rightPanelWidth: 30,
      bottomPanelHeight: 250,
      showDocumentation: true,

      setLeftPanelWidth: (width) => set({ leftPanelWidth: Math.max(20, Math.min(60, width)) }),
      setRightPanelWidth: (width) => set({ rightPanelWidth: Math.max(20, Math.min(60, width)) }),
      setBottomPanelHeight: (height) => set({ bottomPanelHeight: Math.max(150, Math.min(500, height)) }),
      setShowDocumentation: (show) => set({ showDocumentation: show }),
      
      adjustPanelWidth: (panel, delta, containerWidth) => {
        const { leftPanelWidth, rightPanelWidth } = get()
        const deltaPercent = (delta / containerWidth) * 100
        
        if (panel === 'left') {
          const newLeftWidth = Math.max(15, Math.min(65, leftPanelWidth + deltaPercent))
          set({ leftPanelWidth: newLeftWidth })
        } else {
          const newRightWidth = Math.max(15, Math.min(65, rightPanelWidth - deltaPercent))
          set({ rightPanelWidth: newRightWidth })
        }
      },

      adjustBottomPanelHeight: (delta, containerHeight) => {
        const { bottomPanelHeight } = get()
        const newHeight = Math.max(120, Math.min(containerHeight * 0.6, bottomPanelHeight - delta))
        set({ bottomPanelHeight: newHeight })
      }
    }),
    {
      name: 'ui-store'
    }
  )
)
